import { classifySendError } from './outbox-errors';
import { outboxStore, type OutboxStore } from './outbox-store';
import { submissionHeaders, type SubmissionHeaders } from './submission-headers';
import type { OutboxItem } from './outbox.types';

/** Sends one queued item with the given headers; rejects with the axios error. */
export type OutboxSender = (item: OutboxItem, headers: SubmissionHeaders) => Promise<unknown>;

/**
 * Why a flush stopped before the end of the queue:
 * offline (no network), auth (401: wait for a new sign-in), gate
 * (cross-validation required), server (busy or failing: try later).
 */
export type FlushStop = 'offline' | 'auth' | 'gate' | 'server';

export type FlushResult = {
  sent: number;
  failed: number;
  /** Still being processed by the server (409): left for the next flush. */
  inProgress: number;
  stoppedBy: FlushStop | null;
};

/**
 * Sends the owner's pending items one at a time, oldest first, each with its
 * own idempotency key and recorded-at date, the same on every resend.
 * A sent item leaves the queue; see classifySendError for the others.
 */
export async function flushOutbox({
  ownerId,
  send,
  store = outboxStore,
  now = () => new Date(),
}: {
  ownerId: string;
  send: OutboxSender;
  store?: OutboxStore;
  now?: () => Date;
}): Promise<FlushResult> {
  const result: FlushResult = { sent: 0, failed: 0, inProgress: 0, stoppedBy: null };
  const queued = await store.list(ownerId);

  for (const snapshot of queued) {
    // Re-read: the person may have deleted it, or another tab sent it, meanwhile.
    const item = await store.get(ownerId, snapshot.id);
    if (!item || item.status !== 'pending') continue;

    try {
      await send(item, submissionHeaders(item.idempotencyKey, item.createdAt));
      await store.remove(ownerId, item.id);
      result.sent += 1;
      continue;
    } catch (error) {
      const failure = classifySendError(error);
      const lastError = { code: failure.code, ...(failure.status ? { status: failure.status } : {}), at: now().toISOString() };
      const current = await store.get(ownerId, item.id);
      if (!current) continue;

      switch (failure.outcome) {
        case 'final':
          await store.update({ ...current, status: 'failed', attempts: current.attempts + 1, lastError });
          result.failed += 1;
          continue;
        case 'in-progress':
          await store.update({ ...current, attempts: current.attempts + 1, lastError });
          result.inProgress += 1;
          continue;
        case 'gate':
          // Not a failure of this contribution: the attempt is not counted.
          await store.update({ ...current, lastError });
          result.stoppedBy = 'gate';
          return result;
        case 'auth':
          await store.update({ ...current, lastError });
          result.stoppedBy = 'auth';
          return result;
        case 'network':
          await store.update({ ...current, lastError });
          result.stoppedBy = 'offline';
          return result;
        case 'retry-later':
          await store.update({ ...current, attempts: current.attempts + 1, lastError });
          result.stoppedBy = 'server';
          return result;
      }
    }
  }

  return result;
}
